/**
 * Header status chrome for query-backed tiles (S3): a refresh indicator
 * while a run refreshes behind existing data (UI Quality §3 — chrome only,
 * the body never blanks), and stale/error badges with the reason in the
 * tooltip (`title`) — the policy-blocked badge carries the exact policy copy.
 */
import { CircleAlert, RefreshCw, ShieldAlert, Unplug } from 'lucide-react';
import { selectTileRuntime, useDashboardRuntimeStore } from '../../store/dashboardRuntimeStore';

export function TileStatusBadge({ dashboardId, tileId }: { dashboardId: string; tileId: string }) {
  const runtime = useDashboardRuntimeStore(selectTileRuntime(dashboardId, tileId));
  if (!runtime) return null;

  if (runtime.status === 'running' && runtime.lastSuccess) {
    return (
      <span
        className="inline-flex shrink-0 items-center text-muted-foreground"
        title="Refreshing…"
        data-testid="tile-refresh-indicator"
      >
        <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
        <span className="sr-only">Refreshing</span>
      </span>
    );
  }
  if (runtime.status === 'stale' && runtime.staleReason === 'connection-lost') {
    return (
      <span
        className="inline-flex shrink-0 items-center text-warning"
        title="Connection lost — showing the last loaded data"
        data-testid="tile-stale-badge"
      >
        <Unplug className="h-3 w-3" aria-hidden />
        <span className="sr-only">Connection lost</span>
      </span>
    );
  }
  if (runtime.status === 'stale' && runtime.staleReason === 'policy-blocked') {
    return (
      <span
        className="inline-flex shrink-0 items-center text-warning"
        title={runtime.error}
        data-testid="tile-policy-badge"
      >
        <ShieldAlert className="h-3 w-3" aria-hidden />
        <span className="sr-only">{runtime.error}</span>
      </span>
    );
  }
  if (runtime.status === 'error') {
    return (
      <span
        className="inline-flex shrink-0 items-center text-destructive"
        title={runtime.error}
        data-testid="tile-error-badge"
      >
        <CircleAlert className="h-3 w-3" aria-hidden />
        <span className="sr-only">{runtime.error}</span>
      </span>
    );
  }
  return null;
}
